import { CSSProperties, useEffect, useState } from 'react';
import { useFormContext, useFormState } from 'react-hook-form';
import { Size } from '@vivli/shared/theme';
import { TextAreaFormFieldComponent, TextFormFieldComponent } from '@vivli/shared/components';
import { useDataRequestContext, useSafetyConcernsContext } from '@vivli/features/data-requests/infrastructure/context';
import {
    useActiveUserContext,
    useDataRequestsApiContext,
    useModalApiContext,
    useToastApiContext
} from '@vivli/core/infrastructure/context';
import { finalize, first } from 'rxjs/operators';
import { ISafetyConcernReport } from '@vivli/features/data-requests/infrastructure/interface';
import { ButtonView } from '@vivli/shared/views';

const containerStyle: CSSProperties = {
    display: 'grid',
    gridAutoRows: 'auto',
    rowGap: '40px',
    paddingRight: '40px'
};

const twoColStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    columnGap: '20px'
};

const message = (
    <div style={{ display: 'flex', gap: '10px', flexDirection: 'column' }}>
        <div>
            The safety concern screen is provided so that you can alert the contributor of one or more of the studies you are analyzing to
            the possibility of a safety concern with the treatment that was studied. Accordingly, submitting a safety concern generates an
            urgent alert to the contributors of data for your analysis. If you are asking a question, or providing feedback that is not
            safety-related to Vivli or to any of the data contributors, please use the Chat for that.
        </div>
        <div>Is the message you are about to submit a concern about the safety of a treatment?</div>
    </div>
);
export const ReportSafetyConcernFormComponent = () => {
    const { user: activeUser } = useActiveUserContext();

    const { dataRequest } = useDataRequestContext();
    const formApi = useFormContext<ISafetyConcernReport>();
    const { isValid } = useFormState();
    const dataRequestsService = useDataRequestsApiContext();
    const toastService = useToastApiContext();
    const modalService = useModalApiContext();
    const { addSafetyConcern } = useSafetyConcernsContext();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleOnClick = () => {
        modalService.confirm(message, {
            confirmText: 'Yes',
            cancelText: 'No',
            onConfirm: () => handleSubmit()
        });
    };
    const handleSubmit = () => {
        formApi.handleSubmit(({ name, email, phoneNumber, safetyConcernDescription }) => {
            const safetyConcernSubmitter = {
                name,
                email,
                phoneNumber,
                userId: activeUser.userId
            };

            setIsSubmitting(true);
            dataRequestsService
                .postSafetyConcern(dataRequest.id, {
                    safetyConcernSubmitter,
                    safetyConcernDescription
                })
                .pipe(
                    first(),
                    finalize(() => {
                        setIsSubmitting(false);
                    })
                )
                .subscribe(
                    () => {
                        toastService.success('Successfully Submitted your Safety Concern');
                        formApi.reset();
                        formApi.trigger();
                        addSafetyConcern({
                            dataRequestId: dataRequest.id,
                            dataRequestTitle: dataRequest.requestTitle,
                            safetyConcernSubmitter,
                            submittedDateTime: new Date(),
                            safetyConcernDescription
                        });
                    },
                    (e) => {
                        var cleanError = e.replace(/\\r|\\n|"/g, ' ').trim();
                        modalService.error(
                            'An error occured submitting safety concern.  Please try again or contact Vivli support. Message: ' + cleanError
                        );
                    }
                );
        })();
    };

    useEffect(() => {
        formApi.trigger();
    }, []);

    return (
        <div style={containerStyle}>
            <div style={{ marginTop: Size.FIELD_SEPARATION }}>
                <h4>
                    Supply your contact information and safety concern description below, then click 'Submit Safety Concern' to continue.
                </h4>
            </div>

            <div>
                <TextFormFieldComponent name="name" label="Name" />
            </div>

            <div style={twoColStyle}>
                <TextFormFieldComponent name="email" label="Email Address" />

                <TextFormFieldComponent name="phoneNumber" label="Phone Number" />
            </div>

            <div>
                <TextAreaFormFieldComponent name="safetyConcernDescription" label="Describe the Safety Concern" />
            </div>

            <div>
                <ButtonView disabled={!isValid} isLoading={isSubmitting} onClick={handleOnClick}>
                    Submit Safety Concern
                </ButtonView>
            </div>
        </div>
    );
};
